import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { Tooltip } from '@delmonicos/shared-design-system-react/src/components/tooltip/tooltip';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { computeTreePhaseVoltage } from '@delmonicos/shared-sdk/libs/core/src/helpers/compute-phase-voltage';
import { PowerType } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { mapStatusToGenericConnectorStatus } from '@app/feature/charge-point/helper/connector-status.helper';
import { mapTypeToGenericConnectorType } from '@app/feature/charge-point/helper/connector-type.helper';
import { useCpmsBoGetChargePointQuery } from '@service/cpms-bo-charge-point.service';

import * as css from './charge-point-card.module.scss';

interface ChargePointCardComponentProps {
  chargePointId: string;
}

export const ChargePointCardComponent = ({
  chargePointId,
}: ChargePointCardComponentProps): React.JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: chargePoint } = useCpmsBoGetChargePointQuery(chargePointId);

  if (!chargePoint) {
    return null;
  }

  return (
    <Card gap={GapEnum.M}>
      <Stack
        alignItems="flex-start"
        justifyContent="space-between"
        gap={GapEnum.XS}>
        <AccentTitle
          icon="terminal"
          variant={AccentVariantEnum.Secondary}
          text={t('$.ticket-details.charge-point-card.title')}
        />
        <Tag
          direction="column"
          gap={GapEnum.XS}
          className={
            chargePoint.isOnline ? 'bg-surface-success' : 'bg-surface-error'
          }>
          <Text italic fontSize={TextFontSize.Small}>
            {t(
              `delmonicos-shared-sdk.outputs-charge-point-is-online.${chargePoint.isOnline ? 'TRUE' : 'FALSE'}`,
            )}
          </Text>
          <Stack direction="row" gap={GapEnum.XS}>
            {chargePoint.channels
              .flatMap((channel) => channel.physicalSockets)
              .map((socket) => {
                const status = mapStatusToGenericConnectorStatus(
                  socket.ocppStatus,
                );
                const { icon: iconName } = mapTypeToGenericConnectorType(
                  socket.standard,
                );

                const physicalSocketRealVoltage =
                  socket.powerType === PowerType.AC_3_PHASE
                    ? computeTreePhaseVoltage(
                        Number.parseInt(`${socket.voltage}`, 10),
                      )
                    : socket.voltage;

                const physicalSocketPower =
                  socket.powerType &&
                  physicalSocketRealVoltage &&
                  socket.amperage
                    ? (
                        (socket.amperage * physicalSocketRealVoltage) /
                        1000
                      ).toFixed(0)
                    : '?';

                return (
                  <Tag
                    key={socket.id}
                    className={classNames(status.color, css.connectorTag)}>
                    <Tooltip
                      content={`${physicalSocketPower} kW | ${t(
                        `delmonicos-shared-sdk.ocpp-enums-charge-point-status.${socket.ocppStatus ?? 'Unknown'}`,
                      )}`}>
                      <Icon name={iconName} />
                    </Tooltip>
                  </Tag>
                );
              })}
          </Stack>
        </Tag>
      </Stack>
      <Stack direction="column" gap={GapEnum.XXS}>
        <Link
          onClick={() =>
            navigate(
              generatePath(APP_PATH.CHARGE_POINT.DETAILS, {
                id: chargePoint.id,
              }),
            )
          }>
          <Text fontWeight={TextWeight.SemiBold}>{chargePoint.name}</Text>
        </Link>
        <Text italic color={TextColor.Secondary}>
          {chargePoint.identity}
        </Text>
      </Stack>
      <Stack alignItems="center" gap={GapEnum.XS}>
        <Icon
          name="server-06"
          iconSize={IconSize.Small}
          color={IconColor.Secondary}
        />
        <Text color={TextColor.Secondary}>{chargePoint.network.name}</Text>
      </Stack>
      {chargePoint.location && (
        <Stack alignItems="center" gap={GapEnum.XS}>
          <Icon
            name="marker-pin-01"
            iconSize={IconSize.Small}
            color={IconColor.Secondary}
          />
          <Text color={TextColor.Secondary}>{chargePoint.location.name}</Text>
        </Stack>
      )}
    </Card>
  );
};
